<template>
  <transition
    name="sg-message-fade"
    @before-leave="onClose"
    @after-leave="$emit('destroy')"
  >
    <div
      v-show="visible"
      :id="id"
      :class="[
        ns.b(),
        { [ns.m(type)]: type && !icon },
        ns.is('center', center),
        ns.is('closable', closable),
        ns.is('description', isDescription),
        customClass,
      ]"
      :style="customStyle"
      role="alert"
      @mouseenter="clearTimer"
      @mouseleave="startTimer"
    >
      <sg-badge
        v-if="repeatNum > 1"
        :value="repeatNum"
        :type="badgeType"
        :class="ns.e('badge')"
      />
      <sg-icon v-if="iconComponent" :class="ns.e('icon')">
        <component :is="iconComponent" />
      </sg-icon>
      <slot>
        <div :class="ns.e('content')">
          <div :class="ns.em('content', 'title')">{{ content }}</div>
          <div v-if="isDescription" :class="ns.em('content', 'description')">{{ description }}</div>
        </div>
      </slot>
      <sg-icon v-if="closable" :class="ns.e('closebtn')" @click.stop="close">
        <Close />
      </sg-icon>
    </div>
  </transition>
</template>

<script lang="ts">
import { defineComponent, computed, ref, onMounted, watch } from 'vue'
import { useEventListener, useTimeoutFn } from '@vueuse/core'
import { useGlobalComponentSettings } from '@sgui-plus/hooks'
import { EVENT_CODE } from '@sgui-plus/utils/aria'
import SgBadge from '@sgui-plus/components/badge'
import { SgIcon } from '@sgui-plus/components/icon'
import { TypeComponents, TypeComponentsMap } from '@sgui-plus/utils/icon'

import { messageEmits, messageProps } from './message'
import type { BadgeProps } from '@sgui-plus/components/badge'

import type { CSSProperties } from 'vue'

export default defineComponent({
  name: 'SgMessage',

  components: {
    SgBadge,
    SgIcon,
    ...TypeComponents,
  },

  props: messageProps,
  emits: messageEmits,

  setup(props) {
    const { ns, zIndex } = useGlobalComponentSettings('message')
    const { currentZIndex, nextZIndex } = zIndex

    const visible = ref(false)

    const badgeType = ref<BadgeProps['type']>(
      props.type ? (props.type === 'error' ? 'danger' : props.type) : 'info'
    )

    let stopTimer: (() => void) | undefined = undefined

    const iconComponent = computed(() => {
      return props.icon || TypeComponentsMap[props.type] || ''
    })

    const isDescription = computed(() => !!props.description)

    const customStyle = computed<CSSProperties>(() => ({
      top: `${props.top}px`,
      zIndex: currentZIndex.value,
    }))

    function startTimer() {
      if (props.duration > 0) {
        ;({ stop: stopTimer } = useTimeoutFn(() => {
          if (visible.value) close()
        }, props.duration))
      }
    }

    function clearTimer() {
      stopTimer?.()
    }

    function close() {
      visible.value = false
    }

    function keydown({ code }: KeyboardEvent) {
      if (code === EVENT_CODE.esc) {
        // press esc to close the message
        if (visible.value) {
          close()
        }
      } else {
        startTimer() // resume timer
      }
    }

    onMounted(() => {
      startTimer()
      nextZIndex()
      visible.value = true
    })

    watch(
      () => props.repeatNum,
      () => {
        clearTimer()
        startTimer()
      }
    )

    useEventListener(document, 'keydown', keydown)

    return {
      ns,

      iconComponent,
      isDescription,
      customStyle,
      visible,
      badgeType,

      close,
      clearTimer,
      startTimer,
    }
  },
})
</script>
